import React from 'react';
import Autosuggest from 'react-autosuggest';
import { get } from 'lodash';
import { useDebouncedCallback } from 'use-debounce';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import {
  BACKGROUND_PRIMARY,
  QUARTER_CONTRAST,
  HALF_CONTRAST,
  CONTRAST,
} from '../../theme';
import { Star } from '../../parcels';

const SearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0;
  li {
    list-style-type: none;
    padding: 0;
    margin: 0;
  }

  ul {
    padding: 0;
  }
`;

const Cities = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 12px;
`;

const City = styled.div`
  padding: 8px;
  cursor: pointer;
`;

const Row = styled.div`
  border-bottom: 1px solid ${QUARTER_CONTRAST};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 10px 0 10px 6px;
  border-radius: 4px;
  align-items: center;
  background-color: ${({ isHighlighted }) => (isHighlighted ? BACKGROUND_PRIMARY : 'white')};
  color: ${({ isHighlighted }) => (isHighlighted ? CONTRAST : HALF_CONTRAST)};
`;

const ParcelDiv = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: left;
  width: 230px;
`;

const renderSuggestion = (parcel = {}, { isHighlighted }) => {
  const id = get(parcel, 'parcel_id');
  const address = get(parcel, 'add_combo', get(parcel, 'address'));
  return (
    <Row isHighlighted={isHighlighted}>
      <ParcelDiv>
        <div>{address}</div>
        <div>{id}</div>
      </ParcelDiv>
      <Star parcelId={id} />
    </Row>
  );
};

const renderSuggestionsContainer = ({ containerProps, children }) => (
  <div
    // eslint-disable-next-line react/jsx-props-no-spreading
    {...containerProps}
    style={{
      maxHeight: 'calc(100vh - 225px)',
      overflowY: 'scroll',
    }}
  >
    {children}
  </div>
);

const noop = () => { };

function getSuggestionValue(feature) {
  return feature.add_combo || feature.address;
}

const mapState = ({
  searchQuery, searchResults, reports, subunits,
}) => ({
  searchQuery,
  searchResults: searchResults || [],
  reports,
  subunits,
});

const inputTheme = {
  borderWidth: 1,
  borderRadius: 3,
  borderColor: QUARTER_CONTRAST,
  width: 'calc(100% - 16px)',
  fontSize: 16,
  padding: 12,
  boxSizing: 'border-box',
  fontFamily: 'Montserrat, Open Sans, sans-serif',
};

export const Search = () => {
  const {
    searchQuery, searchResults, reports, subunits,
  } = useSelector(
    mapState,
  );
  const dispatch = useDispatch();

  const [executeSearch] = useDebouncedCallback(() => {
    dispatch({
      type: 'FETCH_SEARCH',
      payload: {
        query: searchQuery,
      },
    });
  }, 1000);

  const setValue = (query) => {
    dispatch({
      type: 'SET_SEARCH_QUERY',
      payload: {
        query,
      },
    });
  };

  const setValueAndSearch = (query) => {
    dispatch({
      type: 'SET_SEARCH_QUERY',
      payload: {
        query,
      },
    });
    executeSearch();
  };

  return (
    <SearchContainer>
      <Autosuggest
        theme={{
          input: inputTheme,
        }}
        alwaysRenderSuggestions
        suggestions={searchResults}
        onSuggestionsFetchRequested={({ value, reason }) => {
          if (reason === 'suggestion-selected') {
            const parcel = searchResults.find(
              (p) => p.add_combo === value || p.address === value,
            );

            if (parcel.geom_pt) {
              const [lng, lat] = Array.from(parcel.geom_pt.coordinates);

              dispatch({
                type: 'SET_MAP_SETTINGS',
                payload: {
                  id: 'MAIN_MAP',
                  settings: {
                    zoom: 16,
                    center: {
                      lat,
                      lng,
                    },
                  },
                },
              });
            }
            if (!reports || !reports.length) {
              dispatch({
                type: 'OPEN_MODAL',
                payload: {
                  type: 'REPORTS',
                  data: {
                    config: { parcelId: parcel.parcel_id },
                  },
                },
              });
              return;
            }

            dispatch({
              type: 'OPEN_SIDE_PANEL',
              payload: {
                type: 'PARCEL',
                data: {
                  config: {
                    parcel_id: parcel.parcel_id,
                    report: reports.find(({ id }) => id === 'PARCEL_REPORT'),
                  },
                },
              },
            });
            return;
          }
          setValueAndSearch(value);
        }}
        onSuggestionsClearRequested={noop}
        getSuggestionValue={getSuggestionValue}
        renderSuggestionsContainer={renderSuggestionsContainer}
        renderSuggestion={renderSuggestion}
        inputProps={{
          autoFocus: true,
          placeholder: 'Search for an address',
          value: searchQuery,
          onChange: (_, { newValue, method }) => {
            if (
              method === 'click' ||
              method === 'enter' ||
              method === 'up' ||
              method === 'down'
            ) return;
            setValue(newValue);
          },
        }}
      />
      {subunits && subunits.length ? (
        <Cities>
          <City>
            <strong>Subunits</strong>
          </City>
          {subunits.map(({ settings, name }) => (
            <City
              key={name}
              onClick={() => {
                const action = {
                  type: 'SET_MAP_SETTINGS',
                  payload: {
                    id: 'MAIN_MAP',
                    settings,
                  },
                };
                dispatch(action);
              }}
            >
              {name}
            </City>
          ))}
        </Cities>
      ) : null}
    </SearchContainer>
  );
};
